import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { Spinner } from '@/src/components/ui/spinner'
import { me } from '@/src/api/user'
import { getOperation } from '@/src/api/operation'
import { userCanManageOrganizationSettings } from '@/src/lib/permissionHelpers'
import { OperationForm } from '@/src/components/manage/forms/OperationForm'
import { useTranslation } from '@/src/i18n/client'

export const Route = createFileRoute('/_manage/manage/operations/$operationId/edit')({
  component: OrgOperationEditPage,
})

function OrgOperationEditPage() {
  const { t } = useTranslation('operation')
  const [mode, setMode] = useState<'basic' | 'expert'>('basic')
  const navigate = useNavigate()
  const { operationId } = useParams({ from: '/_manage/manage/operations/$operationId/edit' })

  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const permissions: string[] = (user?.role?.permissions as string[]) ?? []
  const isAdmin = permissions.includes('*')
  const canManageOrg = userCanManageOrganizationSettings(permissions, isAdmin)
  const orgId = user?.organization?.id

  const { data: operation, isLoading: isOperationLoading } = useQuery({
    queryKey: ['operation', operationId],
    queryFn: () => getOperation({ data: { id: operationId } }),
    enabled: !!operationId,
  })

  useEffect(() => {
    if (!isUserLoading && !isOperationLoading) {
      if (!canManageOrg || !orgId) {
        navigate({ to: '/not-permitted' })
        return
      }

      // Additional check: ensure the operation's drawing belongs to the organization
      // Note: getOperation already validates access permissions
    }
  }, [
    isUserLoading,
    isOperationLoading,
    canManageOrg,
    orgId,
    navigate,
  ])

  if (isUserLoading || isOperationLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!canManageOrg || !orgId) return null

  if (!operation) return null

  const goBack = () => {
    navigate({
      to: '/manage/drawings/$id',
      params: { id: operation.drawingId },
      search: { tab: 'data' },
    })
  }

  const handleSuccess = () => {
    goBack()
  }

  const handleCancel = () => {
    goBack()
  }

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.BackButton to={`/manage/drawings/${operation.drawingId}`} />
          <AdminToolbar.Title>{operation.name}</AdminToolbar.Title>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === 'basic' ? 'default' : 'outline'}
              onClick={() => setMode('basic')}
            >
              Basic
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === 'expert' ? 'default' : 'outline'}
              onClick={() => setMode('expert')}
            >
              Expert
            </Button>
          </div>
        </AdminToolbar.Right>
      </AdminToolbar>

      <div className="flex-1 min-h-0">
        <div className="h-full overflow-auto p-4">
          <OperationForm
            drawingId={operation.drawingId}
            operation={operation}
            lockOperationType={!!operation.operationTypeId}
            mode={mode}
            onModeChange={setMode}
            showModeToggle={false}
            onCancel={handleCancel}
            onSuccess={handleSuccess}
          />
        </div>
      </div>
    </div>
  )
}
